/**
 * M-A1 — Create BYOS Skill modal.
 *
 * Sprint 131 Stream C. Minimal viable form: slug, name, vertical, function,
 * category, tags, description, instructions. Advanced fields (activation,
 * requires, output_contract, parameters_schema, assets) are edited from the
 * detail page after creation.
 */

import { useState } from 'react';
import { toast } from 'sonner';
import { DiscardChangesDialog } from '@vanguard-atlas/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useCreateAdminSkill } from '@/features/skills';
import type { SkillCreate } from '@/features/skills';
import { useI18n } from '@/i18n';
import { getErrorMessage } from '@/lib/api-client';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated?: (slug: string) => void;
}

const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,126}[a-z0-9]$/;

export function SkillCreateBYOSModal({ open, onOpenChange, onCreated }: Props) {
  const [slug, setSlug] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [vertical, setVertical] = useState('');
  const [funcVal, setFuncVal] = useState('');
  const [category, setCategory] = useState('general');
  const [tagsStr, setTagsStr] = useState('');
  const [description, setDescription] = useState('');
  const [instructions, setInstructions] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  const { t } = useI18n();

  // ADR-114 enmienda 1: dirty = diff vs estado inicial del formulario.
  const isDirty =
    open &&
    (slug.trim() !== '' ||
      displayName.trim() !== '' ||
      vertical.trim() !== '' ||
      funcVal.trim() !== '' ||
      category !== 'general' ||
      tagsStr.trim() !== '' ||
      description.trim() !== '' ||
      instructions.trim() !== '');

  const discardLabels = {
    title: t('common.discardChanges.title') ?? 'Discard changes?',
    message:
      t('common.discardChanges.message') ??
      'You have unsaved changes. If you close now, they will be lost.',
    confirm: t('common.discardChanges.discard') ?? 'Discard',
    cancel: t('common.discardChanges.keepEditing') ?? 'Keep editing',
  };

  function requestClose() {
    if (isDirty) {
      setConfirmDiscardOpen(true);
      return;
    }
    reset();
    onOpenChange(false);
  }

  const create = useCreateAdminSkill();

  function reset() {
    setSlug('');
    setDisplayName('');
    setVertical('');
    setFuncVal('');
    setCategory('general');
    setTagsStr('');
    setDescription('');
    setInstructions('');
    setErrors({});
  }

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!SLUG_RE.test(slug)) {
      e.slug = t('admin.skills.createByos.errorSlug');
    }
    if (!displayName.trim()) e.displayName = t('admin.skills.createByos.errorDisplayName');
    if (!vertical.trim()) e.vertical = t('admin.skills.createByos.errorVertical');
    if (!funcVal.trim()) e.function = t('admin.skills.createByos.errorFunction');
    if (!category.trim()) e.category = t('admin.skills.createByos.errorCategory');
    if (!instructions.trim() || instructions.trim().length < 10) {
      e.instructions = t('admin.skills.createByos.errorInstructions');
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (!validate()) return;
    const payload: SkillCreate = {
      slug,
      tier: 'byos_skill',
      display_name: displayName,
      description: description.trim() || null,
      vertical,
      function: funcVal,
      category,
      tags: tagsStr
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      instructions,
      instructions_format: 'markdown',
    };
    try {
      const created = await create.mutateAsync({ payload });
      toast.success(`Skill "${created.slug}" created`);
      reset();
      onCreated?.(created.slug);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) {
          requestClose();
          return;
        }
        onOpenChange(v);
      }}
    >
      <DialogContent
        className="max-w-2xl"
        dirty={isDirty}
        discardTitle={discardLabels.title}
        discardMessage={discardLabels.message}
        discardConfirmLabel={discardLabels.confirm}
        discardCancelLabel={discardLabels.cancel}
      >
        <DialogHeader>
          <DialogTitle>{t('admin.skills.createByos.title')}</DialogTitle>
          <DialogDescription>
            {t('admin.skills.createByos.description')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="skill-slug">{t('admin.skills.createByos.slug')}</Label>
              <Input
                id="skill-slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase())}
                placeholder={t('admin.skills.createByos.slugPlaceholder')}
                autoFocus
              />
              {errors.slug && (
                <p className="text-xs text-destructive">{errors.slug}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="skill-name">{t('admin.skills.createByos.displayName')}</Label>
              <Input
                id="skill-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={t('admin.skills.createByos.displayNamePlaceholder')}
              />
              {errors.displayName && (
                <p className="text-xs text-destructive">{errors.displayName}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label htmlFor="skill-vertical">{t('admin.skills.createByos.vertical')}</Label>
              <Input
                id="skill-vertical"
                value={vertical}
                onChange={(e) => setVertical(e.target.value)}
                placeholder={t('admin.skills.createByos.verticalPlaceholder')}
              />
              {errors.vertical && (
                <p className="text-xs text-destructive">{errors.vertical}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="skill-function">{t('admin.skills.createByos.skillFunction')}</Label>
              <Input
                id="skill-function"
                value={funcVal}
                onChange={(e) => setFuncVal(e.target.value)}
                placeholder={t('admin.skills.createByos.functionPlaceholder')}
              />
              {errors.function && (
                <p className="text-xs text-destructive">{errors.function}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="skill-category">{t('admin.skills.createByos.category')}</Label>
              <Input
                id="skill-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              />
              {errors.category && (
                <p className="text-xs text-destructive">{errors.category}</p>
              )}
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="skill-tags">{t('admin.skills.createByos.tags')}</Label>
            <Input
              id="skill-tags"
              value={tagsStr}
              onChange={(e) => setTagsStr(e.target.value)}
              placeholder={t('admin.skills.createByos.tagsPlaceholder')}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="skill-desc">{t('admin.skills.createByos.descriptionLabel')}</Label>
            <Textarea
              id="skill-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={4000}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="skill-instr">{t('admin.skills.createByos.instructions')}</Label>
            <Textarea
              id="skill-instr"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={8}
              placeholder={t('admin.skills.createByos.instructionsPlaceholder')}
              className="font-mono text-sm"
            />
            {errors.instructions && (
              <p className="text-xs text-destructive">{errors.instructions}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={requestClose}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? t('admin.skills.createByos.creating') : t('admin.skills.createByos.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      {/* ADR-114 enmienda 1: confirmación de descarte */}
      <DiscardChangesDialog
        open={confirmDiscardOpen}
        onDiscard={() => {
          setConfirmDiscardOpen(false);
          reset();
          onOpenChange(false);
        }}
        onCancel={() => setConfirmDiscardOpen(false)}
        title={discardLabels.title}
        message={discardLabels.message}
        confirmLabel={discardLabels.confirm}
        cancelLabel={discardLabels.cancel}
      />
    </Dialog>
  );
}
